import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader, { MobileSearchBar } from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import FilterPanel from "@/components/feature/FilterPanel";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import ResourceCard from "@/components/base/ResourceCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { categories } from "@/mocks/resources";
import type { FilterState, SortKey } from "@/types/content";
import {
  EMPTY_FILTER,
  PAGE_SIZE,
  filterResources,
  getCategoryMeta,
  getCategoryResources,
  getFilterOptions,
  paginate,
  sortResources,
  totalPages,
} from "@/utils/content";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "时间最新" },
  { key: "views", label: "点击最多" },
  { key: "rating", label: "评分优先" },
];

export default function CategoryPage() {
  const { type = "" } = useParams();
  const meta = getCategoryMeta(type);
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const tagParam = searchParams.get("tag") || "";

  useDocumentMeta(
    meta ? meta.title : "分类不存在 - 无码高清资源平台",
    meta ? meta.description : "未找到对应的资源分类，请返回首页重新浏览。",
    meta ? meta.keywords : undefined,
  );

  useEffect(() => {
    setFilter({ ...EMPTY_FILTER, tag: tagParam });
    setPage(1);
  }, [tagParam, type]);

  const list = useMemo(() => {
    if (!meta) return [];
    const base = getCategoryResources(meta.key);
    return sortResources(filterResources(base, filter), sort);
  }, [meta, filter, sort]);

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-20">
          <div className="max-w-md w-full text-center bg-background-50 border border-background-200 rounded-lg p-8">
            <span className="mx-auto w-14 h-14 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl" aria-hidden="true"></i>
            </span>
            <h1 className="mt-4 font-heading font-extrabold text-xl text-foreground-950">这个分类还不存在</h1>
            <p className="mt-2 text-sm text-foreground-600 leading-6">
              你访问的分类可能已经更换名称，目前站点提供影视、动漫、音乐三类内容。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Link
                to="/"
                className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to="/category/movie"
                className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm flex items-center cursor-pointer hover:border-primary-300 transition-colors whitespace-nowrap"
              >
                浏览影视资源
              </Link>
            </div>
          </div>
        </main>
        <MobileSearchBar />
        <SiteFooter />
      </div>
    );
  }

  const options = getFilterOptions(meta.key);
  const pages = totalPages(list.length);
  const currentPage = Math.min(page, pages);
  const visible = paginate(list, currentPage);
  const activeCount = [filter.type, filter.region, filter.year, filter.tag].filter(Boolean).length;

  const handleFilterChange = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilter(EMPTY_FILTER);
    setSort("latest");
    setPage(1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 pb-24 md:pb-0">
        <div className="wrap pt-5 md:pt-7">
          <Breadcrumb items={[{ label: "分类" }, { label: `${meta.label}资源` }]} />

          <header className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <span className="w-12 h-12 rounded-md bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
                <i className={`${meta.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="font-heading font-extrabold text-xl md:text-3xl text-foreground-950">
                  {meta.label}资源
                </h1>
                <p className="mt-2 text-sm text-foreground-600 leading-7 max-w-3xl">{meta.intro}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <span className="chip bg-background-50 text-foreground-600 border border-background-200">
                  共 {getCategoryResources(meta.key).length} 条资源
                </span>
                <span className="chip bg-accent-100 text-accent-800">每日更新</span>
              </div>
            </div>
          </header>

          <div className="mt-5 md:mt-7 grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-5 md:gap-7 items-start">
            <aside className="lg:sticky lg:top-24">
              <button
                type="button"
                onClick={() => setMobileFilterOpen((prev) => !prev)}
                className="lg:hidden w-full h-11 mb-3 rounded-md border border-background-300 bg-background-50 flex items-center justify-between px-4 text-sm text-foreground-700 cursor-pointer"
                aria-expanded={mobileFilterOpen}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500" aria-hidden="true"></i>
                  筛选条件
                  {activeCount > 0 && (
                    <span className="chip bg-primary-500 text-background-50">{activeCount}</span>
                  )}
                </span>
                <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
              </button>

              <div className={`${mobileFilterOpen ? "block" : "hidden"} lg:block`}>
                <FilterPanel
                  options={options}
                  value={filter}
                  onChange={handleFilterChange}
                  onReset={handleReset}
                />

                <div className="mt-3 bg-background-50 border border-background-200 rounded-lg p-4">
                  <h2 className="text-xs font-semibold text-foreground-500 tracking-wide mb-2.5">其他分类</h2>
                  <div className="space-y-1.5">
                    {categories
                      .filter((item) => item.key !== meta.key)
                      .map((item) => (
                        <Link
                          key={item.key}
                          to={item.path}
                          className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-background-100 cursor-pointer text-sm text-foreground-700 transition-colors"
                        >
                          <i className={`${item.icon} text-primary-500`} aria-hidden="true"></i>
                          {item.label}资源
                          <i className="ri-arrow-right-s-line ml-auto text-foreground-400" aria-hidden="true"></i>
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
            </aside>

            <section aria-label={`${meta.label}资源列表`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="text-xs md:text-sm text-foreground-500">
                  共找到 <span className="text-primary-600 font-semibold">{list.length}</span> 条结果
                  {activeCount > 0 && <span className="ml-2 text-foreground-400">已应用 {activeCount} 个筛选条件</span>}
                  <span className="ml-2 text-foreground-400">（前端演示数据）</span>
                </div>

                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 self-start sm:self-auto">
                  {sortOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => {
                        setSort(option.key);
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                        sort === option.key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {visible.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                    {visible.map((item) => (
                      <ResourceCard key={item.id} resource={item} />
                    ))}
                  </div>

                  <div className="mt-8">
                    <Pagination page={currentPage} total={pages} onChange={setPage} />
                    <p className="mt-3 text-center text-xs text-foreground-400">
                      第 {currentPage} / {pages} 页 · 每页 {PAGE_SIZE} 条
                    </p>
                  </div>
                </>
              ) : (
                <div className="bg-background-50 border border-background-200 rounded-lg py-14 px-6 text-center">
                  <span className="mx-auto w-14 h-14 rounded-md bg-background-100 text-foreground-400 flex items-center justify-center">
                    <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
                  </span>
                  <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">没有符合筛选条件的资源</h2>
                  <p className="mt-2 text-sm text-foreground-600 leading-6">
                    当前筛选组合下没有匹配内容，试试减少条件，或者重置筛选重新浏览全部{meta.label}资源。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
                    >
                      重置筛选
                    </button>
                    <Link
                      to="/"
                      className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 hover:border-primary-300 text-sm flex items-center cursor-pointer transition-colors whitespace-nowrap"
                    >
                      返回首页
                    </Link>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
      <MobileSearchBar />
      <SiteFooter />
    </div>
  );
}